import {Injectable, signal} from '@angular/core';
import {lastValueFrom, startWith} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {CS, CSCountsResponse, CSFilterParams, CSFiltersResponse, CSPaginatedResponse, CsInformation} from '../../models/cs';
import {environment} from '../../environments/environment';
import {RealtimeService} from './realtime.service';
import {PaginationInfo} from '../../models/la';

@Injectable({
  providedIn: 'root'
})
export class CSService {

  csList = signal<CS[]>([])
  csPagination = signal<PaginationInfo | null>(null)
  csFilters = signal<CSFiltersResponse | null>(null)
  csCounts = signal<CSCountsResponse | null>(null)
  csTypeList = signal<CsInformation[]>([])

  private currentParams: CSFilterParams = { limit: 10, offset: 0 };

  constructor(
    private realtime: RealtimeService,
    private http: HttpClient
  ) {
    this.realtime.cs.pipe(startWith(null))
      .subscribe(()=>{
        this.refreshCurrentPage();
      })
  }

  async refreshCurrentPage() {
    await this.getCSPaginated(this.currentParams);
  }

  async getCSPaginated(params: CSFilterParams = {}): Promise<CSPaginatedResponse> {
    this.currentParams = {
      limit: params.limit ?? this.currentParams.limit,
      offset: params.offset ?? this.currentParams.offset,
      search: params.search || undefined,
      users: params.users?.length ? params.users : undefined,
      status: params.status?.length ? params.status : undefined,
      type: params.type || undefined,
      date: params.date || undefined,
      today: params.today || undefined,
    };
    const queryParams = new URLSearchParams();
    if (this.currentParams.limit !== undefined) queryParams.set('limit', this.currentParams.limit.toString());
    if (this.currentParams.offset !== undefined) queryParams.set('offset', this.currentParams.offset.toString());
    if (this.currentParams.search) queryParams.set('search', this.currentParams.search);
    if (this.currentParams.users && this.currentParams.users.length > 0) queryParams.set('users', this.currentParams.users.join(','));
    if (this.currentParams.status && this.currentParams.status.length > 0) queryParams.set('status', this.currentParams.status.join(','));
    if (this.currentParams.type) queryParams.set('type', this.currentParams.type);
    if (this.currentParams.date) queryParams.set('date', this.currentParams.date);
    if (this.currentParams.today) queryParams.set('today', 'true');
    const queryString = queryParams.toString();
    const url = `${environment.backendUrl}/v2/customer_services${queryString ? `?${queryString}` : ''}`;
    const result = await lastValueFrom(this.http.get<CSPaginatedResponse>(url));
    this.csList.set(result.data);
    this.csPagination.set(result.pagination);
    this.csFilters.set(result.filters);
    this.csCounts.set(result.counts);
    return result;
  }

  async getCSCountByContactId(contactId: string): Promise<number> {
    const result = await lastValueFrom(this.http.get<CSPaginatedResponse>(
      `${environment.backendUrl}/v2/customer_services?contactId=${contactId}&limit=1&offset=0`
    ));
    return result.pagination.total;
  }

  async getCSById(csId: string): Promise<CS> {
    return await lastValueFrom(this.http.get<CS>(`${environment.backendUrl}/customer_services/${csId}`));
  }

  async getAllCsInformation(organizationId?: string){
    const result = await lastValueFrom(this.http.get<CsInformation[]>(`${environment.backendUrl}/cs_information`));
    this.csTypeList.set(result);
  }

  async searchCS(query: string = '', limit: number = 20): Promise<CS[]> {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    params.set('limit', limit.toString());
    return await lastValueFrom(this.http.get<CS[]>(
      `${environment.backendUrl}/v2/customer_services/search?${params.toString()}`
    ));
  }

  async updateCS(id: string,cs: Partial<Omit<CS, 'id' | 'timestamp'>>) {
    const response = await lastValueFrom(this.http.put<CS>(`${environment.backendUrl}/customer_services/${id}`, cs));
    await this.refreshCurrentPage();
    return response;
  }

  async delete(taskId: string) {
    await lastValueFrom(this.http.delete<{}>(`${environment.backendUrl}/customer_services/` + taskId));
    await this.refreshCurrentPage();
  }

  async updateStatus(csId: string, status: string) {
    await lastValueFrom(this.http.put<void>(`${environment.backendUrl}/customer_services/` + csId + '/status', {status}));
    await this.refreshCurrentPage();
  }

  async updateType(csId: string, type: string) {
    await lastValueFrom(this.http.put<void>(`${environment.backendUrl}/customer_services/` + csId + '/type', {type}));
    await this.refreshCurrentPage();
  }

  async updateAssignee(csId: string, assigneeUserId: string) {
    await lastValueFrom(this.http.put<void>(`${environment.backendUrl}/customer_services/` + csId + '/assigneeUserId', {assigneeUserId}));
    await this.refreshCurrentPage();
  }
  async updateContact(csId: string, contactId: string) {
    await lastValueFrom(this.http.put<void>(`${environment.backendUrl}/customer_services/` + csId + '/contactId', {contactId}));
    await this.refreshCurrentPage();
  }

  async updateProtocol(csId: string, protocol: string) {
    await lastValueFrom(this.http.put<void>(`${environment.backendUrl}/customer_services/` + csId + '/protocol', {protocol}));
    await this.refreshCurrentPage();
  }
  async updateSplInsert(csId: string, splInsert: boolean) {
    await lastValueFrom(this.http.put<void>(`${environment.backendUrl}/customer_services/` + csId + '/splInsert', {splInsert}));
    await this.refreshCurrentPage();
  }
}
